import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { MessageService } from 'primeng/api';
import * as L from 'leaflet';

import { MapService } from '../../../services/map.service';
import { SettingsService } from '../../../services/settings.service';
import { RaceProgressRegistration } from 'src/app/models';
import { TrackerService } from '../../../services/tracker.service';
import { TrackerData } from 'src/app/models/types';
import { ColorsService } from '../../../services/colors.service';

/**
 * Универсальный компонент карточки участника
 * Используется в:
 * - race-map (список участников на карте)
 * - progress-list-v2 (список участников в прогрессе)
 */
@Component({
  selector: 'app-universal-competitor-card',
  templateUrl: './universal-competitor-card.component.html',
  styleUrls: ['./universal-competitor-card.component.scss'],
})
export class UniversalCompetitorCardComponent implements OnInit, OnDestroy {
  @Input() competitor!: RaceProgressRegistration;
  @Input() offset: number = 0;
  @Input() timeFormat: string = 'HH:mm:ss';
  @Input() editMode: boolean = false;
  @Input() isOrg: boolean = false;
  @Input() showPosition: boolean = true;
  @Input() showLapsAmount: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() clUid?: string;
  @Input() showTrackToggle: boolean = false;
  
  @Output() onEdit: EventEmitter<RaceProgressRegistration> = new EventEmitter();
  @Output() onNumber: EventEmitter<RaceProgressRegistration> = new EventEmitter();
  @Output() onShowInfo: EventEmitter<string> = new EventEmitter();
  @Output() onShowTrack: EventEmitter<RaceProgressRegistration> = new EventEmitter();
  @Output() onHideTrack: EventEmitter<RaceProgressRegistration> = new EventEmitter();
  @Output() onShowRaceData: EventEmitter<string> = new EventEmitter();
  @Output() onShowHof: EventEmitter<string> = new EventEmitter();

  initials: string = '';
  isCompact = false;
  private resizeObserver?: ResizeObserver;
  private selectAllSubscription?: any;

  @ViewChild('cardContent') cardContent?: ElementRef;

  constructor(
    public settingsService: SettingsService,
    public mapService: MapService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private trackerService: TrackerService,
    private colorsService: ColorsService
  ) {
    this.selectAllSubscription = this.mapService.selectAllModeChange.subscribe((selected) => {
      this.numberEvent(selected, false);
    });
  }

  ngOnInit(): void {
    const arr = this.competitor.registration.user.name.split(' ');
    this.initials = `${arr[0][0]}${arr[1] ? arr[1][0] : ''}`;
    
    // Отслеживаем изменение ширины элемента с задержкой для инициализации
    setTimeout(() => {
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          const newCompact = width <= 400;
          if (newCompact !== this.isCompact) {
            this.isCompact = newCompact;
            console.log('[UniversalCompetitorCard] width:', width, 'isCompact:', this.isCompact);
            this.cdr.detectChanges();
          }
        }
      });
      
      const targetElement = this.cardContent?.nativeElement || this.elementRef.nativeElement;
      this.resizeObserver.observe(targetElement);
    }, 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.selectAllSubscription?.unsubscribe();
  }

  getTrackerData(): TrackerData | undefined {
    const cUid = this.competitor.registration.user.cUid;
    return this.trackerService.getTracker(cUid);
  }

  getPhotoUrl(): string {
    const photo = this.competitor.registration.user.photo;
    if (photo && photo.length > 10) {
      return `${this.settingsService.settings.urlFileDownload}/competitor/uid/${photo}`;
    }
    return `${this.settingsService.settings.urlFileDownload}/competitor/uid/${this.settingsService.settings.emptyUserPhoto}`;
  }

  showInfo(user: any): void {
    this.onShowHof.emit(user.cUid);
  }

  showTrack(): void {
    this.onShowTrack.emit(this.competitor);
  }

  hideTrack(): void {
    this.onHideTrack.emit(this.competitor);
  }

  editCompetitor(): void {
    this.onEdit.emit(this.competitor);
  }

  showRaceData(): void {
    this.onShowRaceData.emit(this.competitor.registration.user.cUid);
  }

  numberEvent(value?: boolean, genEvent = true): void {
    if (!this.competitor.registration.gpsTracker) {
      if (genEvent) {
        this.messageService.add({
          severity: 'error',
          summary: this.settingsService.translate[this.settingsService.selectedLang]['no_tracker'],
        });
      }
      return;
    }
    
    const cUid = this.competitor.registration.user.cUid;
    let trackerData = this.trackerService.getTracker(cUid);
    
    // Если трекера ещё нет в сервисе, создаём его
    if (!trackerData) {
      trackerData = {
        cUid: cUid,
        gpsTracker: this.competitor.registration.gpsTracker,
        numberActive: false,
        points: {},
        pointsKeys: [],
        minTime: 0,
        maxTime: 0,
        startGPStime: this.competitor.startGPStime,
        finGPStime: this.competitor.finGPStime,
        layerGroup: L.featureGroup(),
        number: 0,
        numberColor: '',
        tracksLoaded: false,
        loading: false,
        moveStarted: false,
        reconnectData: false
      };
      this.trackerService.updateTracker(cUid, trackerData);
    } else {
      trackerData.numberActive = value !== undefined ? value : !trackerData.numberActive;
      if (trackerData.numberActive) {
        if (trackerData.numberColor && this.clUid) {
          trackerData.numberColor = this.colorsService.regenerateUniqueColor(
            this.clUid,
            trackerData.numberColor
          );
        } else if (this.clUid) {
          trackerData.numberColor = this.colorsService.generateUniqueColor(this.clUid);
        }
      }
      this.trackerService.updateTracker(cUid, trackerData);
    }

    // Не генерим в случаях когда идет массовое изменения выбора. Это будет через евент сервиса.
    if (genEvent) {
      this.onNumber.emit(this.competitor);
    }
  }

  get noStartText(): string {
    if (this.competitor.registration.user.gender === 1 || this.competitor.registration.user.gender === 0) {
      return this.settingsService.translate[this.settingsService.selectedLang]['no_start_man'];
    }
    if (this.competitor.registration.user.gender === 2) {
      return this.settingsService.translate[this.settingsService.selectedLang]['no_start_woman'];
    }
    return '';
  }
}
